import React, { useEffect, useState } from 'react';
import { getPartnerSettings, updatePartnerSettings } from '@/services/partnerAdminService';
import { usePartnerAdmin } from '@/contexts/PartnerAdminContext';

function Toggle({ label, description, enabled, onChange, disabled }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', borderBottom: '1px solid #1a1a1a',
    }}>
      <div>
        <div style={{ fontSize: 13, color: '#ccc', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 11, color: '#555', marginTop: 3 }}>{description}</div>
      </div>
      <button
        onClick={() => !disabled && onChange(!enabled)}
        disabled={disabled}
        style={{
          width: 44, height: 24, borderRadius: 12, border: 'none',
          background: enabled ? '#c8f04a' : '#2a2a2a',
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative', transition: 'background 0.2s',
          flexShrink: 0, opacity: disabled ? 0.5 : 1,
        }}
      >
        <div style={{
          position: 'absolute', top: 3,
          left: enabled ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%',
          background: enabled ? '#0d0d0d' : '#555',
          transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );
}

function DomainManager({ domains, onChange, disabled }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const addDomain = () => {
    const val = input.trim().toLowerCase();
    if (!val) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(val)) {
      setError('Invalid domain format (e.g. ucl.ac.uk)');
      return;
    }
    if (domains.includes(val)) {
      setError('Domain already added.');
      return;
    }
    onChange([...domains, val]);
    setInput('');
    setError('');
  };

  const removeDomain = (d) => onChange(domains.filter(x => x !== d));

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 13, color: '#ccc', fontWeight: 500, marginBottom: 4 }}>
        Email Domain Restrictions
      </div>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 12 }}>
        Restrict sign-up to specific email domains. Leave empty to allow any domain.
      </div>

      {domains.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {domains.map(d => (
            <div key={d} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#1a2a0d', border: '1px solid #2a3a1a',
              borderRadius: 4, padding: '3px 8px',
            }}>
              <span style={{ fontSize: 12, color: '#c8f04a' }}>{d}</span>
              {!disabled && (
                <button
                  onClick={() => removeDomain(d)}
                  style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}
                >✕</button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={input}
            onChange={e => { setInput(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && addDomain()}
            placeholder="e.g. ucl.ac.uk"
            style={{
              flex: 1, background: '#0d0d0d', border: '1px solid #222',
              color: '#ccc', padding: '7px 10px', borderRadius: 5,
              fontSize: 12, outline: 'none', fontFamily: 'inherit',
            }}
          />
          <button
            onClick={addDomain}
            style={{
              background: '#1a2a0d', border: '1px solid #2a3a1a',
              color: '#c8f04a', padding: '7px 12px', borderRadius: 5,
              fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >Add</button>
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: '#f04a4a', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

export default function PartnerSettingsPage() {
  const { membership } = usePartnerAdmin();
  const isAdmin = membership?.is_admin;

  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    getPartnerSettings()
      .then(setSettings)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => {
    setSettings(s => ({ ...s, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updatePartnerSettings(settings);
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="pa-page-loading">Loading settings...</div>;
  if (!settings) return <div className="pa-page-error">{error || 'Failed to load settings.'}</div>;

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{
        background: '#111', border: '1px solid #1e1e1e',
        borderRadius: 8, overflow: 'hidden', marginBottom: 20,
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #1e1e1e',
          fontSize: 10, color: '#555', textTransform: 'uppercase',
          letterSpacing: '0.07em', fontWeight: 600,
        }}>
          Sign-In Methods
        </div>

        <Toggle
          label="Google Sign-In"
          description="Allow users to sign in and register with their Google account."
          enabled={settings.google_login_enabled}
          onChange={v => handleChange('google_login_enabled', v)}
          disabled={!isAdmin}
        />

        <Toggle
          label="LinkedIn Sign-In"
          description="Allow users to sign in and register with their LinkedIn account."
          enabled={settings.linkedin_login_enabled}
          onChange={v => handleChange('linkedin_login_enabled', v)}
          disabled={!isAdmin}
        />
      </div>

      <div style={{
        background: '#111', border: '1px solid #1e1e1e',
        borderRadius: 8, overflow: 'hidden', marginBottom: 24,
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #1e1e1e',
          fontSize: 10, color: '#555', textTransform: 'uppercase',
          letterSpacing: '0.07em', fontWeight: 600,
        }}>
          Access Control
        </div>
        <DomainManager
          domains={settings.allowed_email_domains}
          onChange={v => handleChange('allowed_email_domains', v)}
          disabled={!isAdmin}
        />
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: '#1a2a0d', border: '1px solid #2a3a1a',
              color: '#c8f04a', padding: '9px 20px', borderRadius: 6,
              fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1, fontFamily: 'inherit',
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          {saved && <span style={{ fontSize: 12, color: '#88c840' }}>✓ Saved</span>}
          {error && <span style={{ fontSize: 12, color: '#f04a4a' }}>{error}</span>}
        </div>
      )}

      <style>{`
        .pa-page-loading { color: #555; font-size: 13px; padding: 40px 0; }
        .pa-page-error   { color: #f04a4a; font-size: 13px; padding: 40px 0; }
      `}</style>
    </div>
  );
}